<template>
  <BaseButton :loading="isDownloading" class="mx-0" @click="handleDownload">
    <BaseIcon icon="cloud-arrow-down" tag="i" class="mr-2" />
    Download
  </BaseButton>
</template>

<script>
import { defineComponent, computed, ref } from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import { saveAs } from 'file-saver'

import { useToast, useRLSGroup } from '@composables'
import { LincolnRLSGroupsService } from '@services'

import { BaseButton, BaseIcon } from '@components/base'

export default defineComponent({
  name: 'CompanyRLSDownloadButton',
  components: {
    BaseButton,
    BaseIcon
  },
  setup() {
    const { useGetters } = createVuexHelpers()

    const { company } = useGetters(['company'])

    const { showToast } = useToast()

    // RLS - TPAs
    const { rlsGroupItems: tpaRLSGroupItems } = useRLSGroup('TPA')

    const tpaIds = computed(() =>
      tpaRLSGroupItems.value
        .filter((tpaRLSGroupItem) => {
          return (company.value.rls_tpa_ids || []).includes(tpaRLSGroupItem.id)
        })
        .map((tpaRLSGroupItem) => tpaRLSGroupItem.id)
    )

    // RLS - Underwriters
    const { rlsGroupItems: underwriterRLSGroupItems } =
      useRLSGroup('Underwriter')

    const underwriterIds = computed(() =>
      underwriterRLSGroupItems.value
        .filter((underwriterRLSGroupItem) => {
          return (company.value.rls_underwriter_ids || []).includes(
            underwriterRLSGroupItem.id
          )
        })
        .map((underwriterRLSGroupItem) => underwriterRLSGroupItem.id)
    )

    // RLS - Coverholders
    const { rlsGroupItems: coverholderRLSGroupItems } =
      useRLSGroup('Coverholder')

    const coverholderIds = computed(() =>
      coverholderRLSGroupItems.value
        .filter((coverholderRLSGroupItem) => {
          return (company.value.rls_coverholder_ids || []).includes(
            coverholderRLSGroupItem.id
          )
        })
        .map((coverholderRLSGroupItem) => coverholderRLSGroupItem.id)
    )

    // RLS - Brokers
    const { rlsGroupItems: brokerRLSGroupItems } = useRLSGroup('Broker')

    const brokerIds = computed(() =>
      brokerRLSGroupItems.value
        .filter((brokerRLSGroupItem) => {
          return (company.value.rls_broker_ids || []).includes(
            brokerRLSGroupItem.id
          )
        })
        .map((brokerRLSGroupItem) => brokerRLSGroupItem.id)
    )

    // Download
    const isDownloading = ref(false)

    const handleDownload = async () => {
      isDownloading.value = true

      try {
        const { data } = await LincolnRLSGroupsService.download(
          company.value.name,
          tpaIds.value,
          underwriterIds.value,
          coverholderIds.value,
          brokerIds.value
        )

        isDownloading.value = false

        saveAs(data, `${company.value.name} - RLS.xlsx`)
      } catch (e) {
        const { response } = e || {}

        isDownloading.value = false

        if (!!response) {
          const { data } = response || {}
          const { data: error } = data || {}

          showToast(error.message, 'error')
        } else {
          showToast('Download failed.', 'error')
        }
      }
    }

    return {
      isDownloading,
      handleDownload
    }
  }
})
</script>
